import React, { useEffect, useState } from "react";
import styles from "./InvoicesComponents.module.scss";
import DatePicker from "react-datepicker";
import { Button, Input, Label } from "reactstrap";
import moment from "moment";
import DailyProfitSharingTable from "./ProfitSharingComponents/DailyProfitSharingTable";
import axios from "axios";
import { jsPDF } from "jspdf";

export default function ProfitSharingNotice() {
  const [selectedMonth, setSelectedMonth] = useState(
    moment().add("-1", "month").toDate()
  );
  // Profit Sharing Notice Number
  const [profitSharingNoticeNumber, setProfitSharingNoticeNumber] = useState(0);
  // Issue Date
  const [issueDate, setIssueDate] = useState(new Date());
  // finances
  const [totalAmount, setTotalAmount] = useState(0);
  const [clientAmount, setClientAmount] = useState(0);
  const [cceAmount, setCceAmount] = useState(0);
  const [aeqonAmount, setAeqonAmount] = useState(0);
  const [facilityRebates, setFacilityRebates] = useState({});
  // Remarks
  const [remarks, setRemarks] = useState("Nil");
  // Loading state
  const [loading, setLoading] = useState(false);
  const [lastSaved, setLastSaved] = useState("");

  async function getProfitSharingNoticeData(month) {
    // get new daily values
    const response = await axios.post(
      "/invoices/generateProfitSharingNotice?selectedMonth=" + month
    );

    if (response.status === 200) {
      setProfitSharingNoticeNumber(Number(response.data.paymentNoticeCount) + 1);
      setTotalAmount(response.data.totalAmount);
      setClientAmount(response.data.clientAmount);
      setCceAmount(response.data.cceAmount);
      setAeqonAmount(response.data.aeqonAmount);
      setFacilityRebates(response.data.facilityRebates);
    }
  }

  async function getLastSavedProfitSharingNotice(month) {
    setLastSaved("");

    const response = await axios.post(
      "/invoices/lastSavedProfitSharingNotice?selectedMonth=" + month
    );

    if (response.status === 200 && response.data !== null) {
      setLastSaved(`Last saved: ${moment(response.data.createdAt).format("DD/MM/YYYY HH:mm:ss")}. Notice number: ${response.data.notice_number}.`);
    } else {
      setLastSaved("");
    }
  }

  async function handleChangeMonth(month) {
    // change month
    setSelectedMonth(month);
  }

  function onInputChange(e, setStateFunction) {
    setStateFunction(e.target.value);
  }

  async function handleConvertToPDF() {
    // Set loading to true
    setLoading(true);
    
    try {
      const response = await axios.post(
        "/invoices/generateProfitSharingPDF?selectedMonth=" + selectedMonth,
        {
          selectedMonth: selectedMonth,
          facilityRebates: facilityRebates,
          totalAmount: totalAmount,
          clientAmount: clientAmount,
          cceAmount: cceAmount,
          aeqonAmount: aeqonAmount,
          profitSharingNoticeNumber: profitSharingNoticeNumber,
          issueDate: issueDate,
          remarks: remarks,
        }
      );

      if (response.status === 200) {
        const doc = new jsPDF({
          unit: "mm",
          format: [210, 297],
          putOnlyUsedFonts: true,
          compress: true,
        });

        doc.addPage();
        doc.html(response.data.htmlPage1, {
          callback: function (pdf) {
            pdf.html(response.data.htmlPage2, {
              callback: function (pdf2) {
                // Set loading to false only after download starts
                pdf2.save(
                  `Aeqon/CCE Profit Share - ${moment(selectedMonth).format(
                    "MMM YYYY"
                  )}`
                );
                setLoading(false);
              },
              x: 12.5,
              y: 310,
              width: 185,
              windowWidth: 850,
              margin: [0, 10, 10, 0],
            });
          },
          x: 12.5,
          y: 10,
          width: 185,
          windowWidth: 850,
          margin: [0, 10, 10, 0],
        });
      }
    } catch (error) {
      // In case of error, also set loading to false
      setLoading(false);
    } finally {
      setProfitSharingNoticeNumber(profitSharingNoticeNumber+1);
      getLastSavedProfitSharingNotice(selectedMonth);
    }
  }

  useEffect(() => {
    getProfitSharingNoticeData(selectedMonth);
    getLastSavedProfitSharingNotice(selectedMonth);
  }, [selectedMonth]);

  return (
    <div className={styles.PaymentNoticeContainer}>
      <div className={styles.GenerateContainer}>
        <div className={styles.SelectContainer}>
          <div className={styles.MonthSelectContainer}>
            <div>Month:</div>
            <DatePicker
              dateFormat="MM/yyyy"
              selected={selectedMonth}
              onChange={(month) => handleChangeMonth(month)}
              showMonthYearPicker
              maxDate={moment().add("-2", "weeks").toDate()}
            />
          </div>
        </div>
      </div>

      {facilityRebates.length !== 0 && (
        <div className={styles.FormInputContainer}>
          <h3>
            Profit Sharing for {moment(selectedMonth).format("MMMM YYYY")}
          </h3>
          <div className={styles.SelectContainer}>
            <div className={styles.MonthSelectContainer}>
              <div>Profit Sharing Notice Number:</div>
              <Input
                type="number"
                value={profitSharingNoticeNumber}
                onChange={(e) => onInputChange(e, setProfitSharingNoticeNumber)}
                step={1}
                disabled
              />
            </div>
            {lastSaved && <div className={styles.FormInputContainer}>{lastSaved}</div>}
            <div className={styles.FormInputContainer}>
              <Label>Issue Date:</Label>
              <div>
                <DatePicker
                  selected={issueDate}
                  onChange={(date) => setIssueDate(date)}
                  inline
                />
              </div>
            </div>
            <div className={styles.RemarksContainer}>
              <div>Remarks:</div>
              <Input
                type="text"
                value={remarks}
                onChange={(e) => onInputChange(e, setRemarks)}
              />
            </div>
          </div>
          <DailyProfitSharingTable
            totalAmount={totalAmount}
            setTotalAmount={setTotalAmount}
            clientAmount={clientAmount}
            setClientAmount={setClientAmount}
            cceAmount={cceAmount}
            setCceAmount={setCceAmount}
            aeqonAmount={aeqonAmount}
            setAeqonAmount={setAeqonAmount}
            facilityRebates={facilityRebates}
            setFacilityRebates={setFacilityRebates}
            onInputChange={onInputChange}
            selectedMonth={selectedMonth}
          />
          {Object.keys(facilityRebates).length > 0 && (
            <div className={styles.ConvertButtonContainer}>
              <Button onClick={handleConvertToPDF}>
                {loading ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                    Printing...
                  </>
                ) : (
                  "Save & Print"
                )}
              </Button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
